import {Alert, Box, Stack} from "@mui/material";
import React, {useCallback, useMemo} from "react";
import {useSearchParams} from "react-router-dom";
import {DateRangeOptions} from "../../components/filter-fields/DateRangeFilter";
import {useFilter} from "../../util/FilterSerializer";
import {FloorModel} from "../floors/FloorModel";
import {FloorSummaries} from "../floors/FloorsPage";
import {filterFloorSummaries, FloorsFilter, floorsFilterSerializer} from "../floors/FloorsFilter";
import {useSeatingPlanDeskModel} from "./DesksTab";
import {SeatingPlanModel} from "./SeatingPlanModel";
import {SeatingPlanStickyFilter} from "./SeatingPlanStickyFilter";
import {SeatingPlanTab} from "./SeatingPlanTab";
import {FloorPlanViewer} from "../floors/FloorPlanViewer";
import {SeatingPlanTabLabel} from "./SeatingPlanTabLabel";
import {Loading} from "../../components/loading-data/LoadingData";
import {buildSetState, SetState} from "../../util/FormHandlers";
import {Opt} from "../../util/Optional";
import {HeaderBreadcrumbs} from "../../components/page/HeaderBreadcrumbs";

function useSeatingPlanFloorModel(model: SeatingPlanModel) {
    const deskModel = useSeatingPlanDeskModel(model, DateRangeOptions.lastFourMonths);

    return useMemo(() => {
        return deskModel.map(deskModel =>
            FloorModel.allFloors(deskModel, model.desks)
        );
    }, [deskModel, model.desks]);
}

function useSelectedFloorId(): [Opt<string>, SetState<Opt<string>>] {
    const [searchParams, setSearchParams] = useSearchParams();
    return useMemo(() => {
        const floorId: Opt<string> = searchParams.get('floorId');
        const setFloorId = buildSetState<Opt<string>>(
            newValueFn => {
                const newFloorId = newValueFn(floorId);
                const params = new URLSearchParams(searchParams);
                params.set('tab', 'floors');
                if (newFloorId != null) {
                    params.set('floorId', newFloorId);
                } else {
                    params.delete('floorId');
                }
                setSearchParams(params);
            }
        );
        return [floorId, setFloorId];
    }, [searchParams, setSearchParams]);
}

function FloorsTab({model}: { model: SeatingPlanModel }) {
    const [selectedFloorId, setSelectedFloorId] = useSelectedFloorId();
    const setSelectedFloor = useCallback((floor?: FloorModel) => {
        setSelectedFloorId(floor?.floor?.id);
    }, [setSelectedFloorId]);
    const filterSerializer = useMemo(
        () => floorsFilterSerializer(model.hoodItems),
        [model.hoodItems],
    );
    const [filter, setFilter] = useFilter(filterSerializer);

    const floorModels = useSeatingPlanFloorModel(model);
    const filteredFloorModels = useMemo(
        () => floorModels.map(floorModels => {
            const filtered = filterFloorSummaries(floorModels, filter);
            const selectedFloor = floorModels.find(floor => floor.floor.id === selectedFloorId);
            return {
                unfiltered: floorModels,
                filtered,
                selectedFloor,
            };
        }),
        [floorModels, filter, selectedFloorId],
    );

    return (
        <Loading value={filteredFloorModels}>{({unfiltered, filtered, selectedFloor}) =>
            !selectedFloor ? (
                <>
                    <SeatingPlanStickyFilter>
                        <FloorsFilter
                            filter={filter}
                            setFilter={setFilter}
                            allNeighbourhoods={model.hoodItems}
                        />
                    </SeatingPlanStickyFilter>
                    {filtered.length > 0 ? (
                        <Box mt={2}>
                            <FloorSummaries floorSummaries={filtered} selectFloor={setSelectedFloor}/>
                        </Box>
                    ) : (
                        <Alert severity={unfiltered.length > 0 ? "warning" : "info"} sx={{my: 2}}>
                            {unfiltered.length > 0
                                ? 'No floors match the current filters. Try adjusting your filter criteria.'
                                : 'No floors found.'}
                        </Alert>
                    )}
                </>
            ) : (
                <Stack spacing={2}>
                    <HeaderBreadcrumbs variant="h5" breadcrumbs={[
                        HeaderBreadcrumbs.link('Floors', setSelectedFloor),
                        HeaderBreadcrumbs.menu(selectedFloor, unfiltered, floor => ({
                            key: floor.floor.id,
                            label: floor.floor.name,
                            onClick: () => setSelectedFloor(floor),
                        })),
                    ]}/>
                    <FloorPlanViewer
                        key={selectedFloor.floor.id}
                        src={selectedFloor.floor.floorPlanUrl}
                        alt={`Floor plan for ${selectedFloor.floor.name}`}
                        floor={selectedFloor}
                    />
                </Stack>
            )
        }</Loading>
    )
}

export const floorsTab: SeatingPlanTab<any> = {
    label: (_, active) => (
        <SeatingPlanTabLabel label="Floors" active={active}/>
    ),
    key: 'floors',
    actions: {},
    content: FloorsTab,
};
